import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { labelOf } from "../settings/catalog";
import { useSettings } from "../settings/context";
import { useMode } from "./mode";

/**
 * ═══ LES TEXTES QUI SE MODIFIENT DANS LA PAGE ═══
 *
 * Plus de la moitié des lignes du panneau étaient des textes que l'aperçu affiche déjà :
 * un titre, un sous-titre, un message. En mode organisateur, ils se modifient là où on
 * les lit : un clic, on tape, Entrée valide, Échap annule. Le panneau les replie dans
 * une ligne de leur famille (voir `Group.tsx`), d'où l'on peut toujours les déplier.
 *
 * ⚠️ RIEN NE S'ÉCRIT PENDANT LA FRAPPE, seulement à la validation. Le texte appartient
 * alors au navigateur ; un nouveau rendu de React au milieu de la frappe aurait déplacé
 * le curseur ou effacé ce qui vient d'être tapé. À la validation, l'élément est RECRÉÉ
 * (`key`) plutôt que corrigé : après une édition libre, React ne sait plus quel nœud de
 * texte il possède, et le corriger en place laissait parfois l'ancien texte à l'écran.
 *
 * ⚠️ L'ÉCRITURE PASSE PAR LE MÊME `write` QUE LE PANNEAU : même portée (langue,
 * typologie), même mode d'enregistrement. Le bandeau « Surcharge » de l'éditeur dit
 * déjà où elle atterrit.
 *
 * ⚠️ DES TITRES ET DES MESSAGES SEULEMENT, jamais le libellé d'un bouton ou d'un lien :
 * un texte éditable DANS un bouton se bat avec le bouton (la barre d'espace l'active,
 * Firefox refuse d'y placer le curseur). Ceux-là restent dans le panneau et la carte.
 *
 * Displayed strings are French: the organiser is a French speaker.
 */

type Registrar = (key: string) => () => void;

const RegistrarContext = createContext<Registrar>(() => () => undefined);
const KeysContext = createContext<ReadonlySet<string>>(new Set());

/**
 * Tient la liste des textes que l'écran affiché sait modifier sur place, pour que le
 * panneau les replie. Un compteur par clé : un même texte peut être monté deux fois
 * (bureau et mobile), et le premier démonté ne doit pas l'effacer de la liste.
 */
export function InPlaceProvider({ children }: { children: ReactNode }) {
  const counts = useRef(new Map<string, number>());
  const [keys, setKeys] = useState<ReadonlySet<string>>(new Set());

  const register = useCallback<Registrar>((key) => {
    const map = counts.current;
    map.set(key, (map.get(key) ?? 0) + 1);
    if (map.get(key) === 1) setKeys(new Set(map.keys()));
    return () => {
      const left = (map.get(key) ?? 1) - 1;
      if (left > 0) map.set(key, left);
      else {
        map.delete(key);
        setKeys(new Set(map.keys()));
      }
    };
  }, []);

  return (
    <RegistrarContext.Provider value={register}>
      <KeysContext.Provider value={keys}>{children}</KeysContext.Provider>
    </RegistrarContext.Provider>
  );
}

/** Les clés que l'écran affiché modifie sur place. Vide hors du mode organisateur. */
export function useInPlaceKeys(): ReadonlySet<string> {
  return useContext(KeysContext);
}

type Tag = "h2" | "p" | "span";

export function InPlace({
  settingKey,
  text,
  as: Element = "span",
  className,
  multiline = false,
  label,
}: {
  /** La clé écrite, instance comprise : celle que l'écran lit. */
  settingKey: string;
  /** Le texte affiché, repli compris. */
  text: string;
  as?: Tag;
  className?: string;
  /** Entrée valide ; avec plusieurs lignes, Maj+Entrée passe à la ligne. */
  multiline?: boolean;
  /** Le nom du réglage, quand la clé (une instance) n'est pas dans le catalogue. */
  label?: string;
}) {
  const mode = useMode();
  const register = useContext(RegistrarContext);
  const { write, clear, detail } = useSettings();
  const [editing, setEditing] = useState(false);
  /* Relancé à chaque validation ou annulation : l'élément est recréé, voir plus haut. */
  const [round, setRound] = useState(0);
  const ref = useRef<HTMLElement | null>(null);
  /* Fermé une fois : la recréation de l'élément peut lui faire perdre le focus, et ce
     `blur` tardif ne doit pas écrire ce qu'Échap vient d'annuler. */
  const closed = useRef(false);
  const organiser = mode === "organiser";
  const name = label ?? (labelOf(settingKey) || "Texte");

  useEffect(
    () => (organiser ? register(settingKey) : undefined),
    [organiser, settingKey, register],
  );

  useEffect(() => {
    const el = ref.current;
    if (!editing || !el) return;
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }, [editing]);

  const handlers = useMemo(() => {
    const read = () => {
      /* `innerText` garde les retours à la ligne tapés ; `textContent` en repli (jsdom
         ne connaît pas le premier). */
      const el = ref.current;
      const raw = el?.innerText ?? el?.textContent ?? "";
      return multiline ? raw.trim() : raw.replace(/\s+/g, " ").trim();
    };
    const close = () => {
      closed.current = true;
      setEditing(false);
      setRound((r) => r + 1);
    };
    const commit = () => {
      if (closed.current) return;
      const next = read();
      close();
      if (next === text.trim()) return;
      /* Vidé : on retire ce qui est écrit à ce niveau, et le texte d'en dessous (la
         langue de référence, le défaut) reparaît. Un texte vide écrit exprès n'aurait
         pas de sens pour un titre. */
      if (next === "") {
        if (detail(settingKey).isSet) clear(settingKey);
        return;
      }
      write(settingKey, next);
    };
    return { commit, cancel: close };
  }, [multiline, text, settingKey, write, clear, detail]);

  if (!organiser)
    return text ? <Element className={className}>{text}</Element> : null;

  const empty = text.trim() === "";
  const classes = [className, "inplace", empty && !editing ? "inplace--empty" : ""]
    .filter(Boolean)
    .join(" ");

  return (
    <Element
      key={round}
      ref={(el: HTMLElement | null) => {
        ref.current = el;
      }}
      className={classes}
      data-inplace={settingKey}
      data-editing={editing ? "1" : undefined}
      data-hint={`${name} · Entrée pour valider, Échap pour annuler`}
      title={editing ? undefined : `${name} : cliquer pour modifier`}
      contentEditable={editing ? "plaintext-only" : undefined}
      suppressContentEditableWarning
      onClick={(e) => {
        /* Le clic ne doit rien déclencher d'autre : ni lien, ni navigation, ni la
           sélection d'une option autour. */
        e.preventDefault();
        e.stopPropagation();
        if (!editing) {
          closed.current = false;
          setEditing(true);
        }
      }}
      onBlur={editing ? handlers.commit : undefined}
      onKeyDown={(e) => {
        if (!editing) return;
        e.stopPropagation();
        if (e.key === "Escape") {
          e.preventDefault();
          handlers.cancel();
        } else if (e.key === "Enter" && !(multiline && e.shiftKey)) {
          e.preventDefault();
          ref.current?.blur();
        }
      }}
    >
      {empty ? (editing ? "" : `+ ${name}`) : text}
    </Element>
  );
}
